import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, Navigate, Outlet, RouterProvider, createBrowserRouter, createRoutesFromElements, useLocation, useParams, useRouteError } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppConfigProvider } from "@/contexts/AppConfigContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { TamanhoTextoProvider } from "@/contexts/TamanhoTextoContext";
import AvisoDesignNovo from "@/components/AvisoDesignNovo";
import DynamicHead from "@/components/DynamicHead";
import ScrollToTop from "@/components/ScrollToTop";
import { lazy, Suspense, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/contexts/LocaleContext";

// ── Critical path — loaded eagerly (user lands here immediately) ──────────────
import Login from "./pages/Login";
import Inicio from "./pages/Inicio";
import NotFound from "./pages/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import PageSkeleton from "./components/PageSkeleton";
import Chrome from "./components/Chrome";
import { CEM_PREFIX } from "./lib/cleanMornings";
import AvisoAtualizacao from "./components/AvisoAtualizacao";
import GlucofySplashLoading from "./components/GlucofySplashLoading";
import DailyFeedbackPopup from "./components/DailyFeedbackPopup";
import EarlySatisfactionPopup from "./components/EarlySatisfactionPopup";
import { FeatureRatingManager } from "./components/FeatureRatingSheet";
import OnboardingExperience from "./components/OnboardingExperience";
import { useAutoLocale } from "@/hooks/useAutoLocale";
import { ImageGenerationProvider } from "@/contexts/ImageGenerationContext";
import RecipeDetailErrorBoundary from "@/components/RecipeDetailErrorBoundary";
import { buscarTodas } from "@/lib/paginacao";

// ── Lazy-loaded pages — split into separate chunks, loaded on navigation ──────
const Recetas            = lazy(() => import("./pages/Recetas"));
const RecipeCategory     = lazy(() => import("./pages/RecipeCategory"));
const RecipeDetail       = lazy(() => import("./pages/RecipeDetail"));
const RecipeCooking      = lazy(() => import("./pages/RecipeCooking"));
const Feedback           = lazy(() => import("./pages/Feedback"));
const FeedbackThread     = lazy(() => import("./pages/FeedbackThread"));
const Perfil             = lazy(() => import("./pages/Perfil"));
const BlockDetail        = lazy(() => import("./pages/BlockDetail"));
const Viewer             = lazy(() => import("./pages/Viewer"));
const Admin              = lazy(() => import("./pages/Admin"));
const LinkTree           = lazy(() => import("./pages/LinkTree"));
const ReposteriaCero     = lazy(() => import("./pages/ReposteriaCero"));
const ReposteriaCeroDetail = lazy(() => import("./pages/ReposteriaCeroDetail"));
const RemedyLibrary       = lazy(() => import("./components/remedies/RemedyLibrary"));
const RemedyDetail        = lazy(() => import("./components/remedies/RemedyDetail"));
const RemedyBuriedList    = lazy(() => import("./components/remedies/RemedyBuriedList"));
const RemedyHowToMeasure  = lazy(() => import("./components/remedies/RemedyHowToMeasure"));
const SavedRemedies      = lazy(() => import("./pages/SavedRemedies"));
const Kuchengluck        = lazy(() => import("./pages/Kuchengluck"));
const Brotgluck          = lazy(() => import("./pages/Brotgluck"));
const CollectionDetail   = lazy(() => import("./pages/CollectionDetail"));
const CleanCakesBonuses  = lazy(() => import("./pages/cleanCakes/CleanCakesBonuses"));
const CleanCakesBonusDetail = lazy(() => import("./pages/cleanCakes/CleanCakesBonusDetail"));
const CleanMorningsHome      = lazy(() => import("./pages/cleanMornings/CleanMorningsHome"));
const CleanMorningsCategoria = lazy(() => import("./pages/cleanMornings/CleanMorningsCategoria"));
const CleanMorningsMassa     = lazy(() => import("./pages/cleanMornings/CleanMorningsMassa"));
const CleanMorningsLanches   = lazy(() => import("./pages/cleanMornings/CleanMorningsLanches"));
const CleanMorningsBonuses   = lazy(() => import("./pages/cleanMornings/CleanMorningsBonus").then((m) => ({ default: m.CleanMorningsBonuses })));
const CleanMorningsBonusDetail = lazy(() => import("./pages/cleanMornings/CleanMorningsBonus").then((m) => ({ default: m.CleanMorningsBonusDetail })));
const CleanMealsBonuses  = lazy(() => import("./pages/cleanMeals/CleanMealsBonuses"));
const CleanMealsBonusDetail = lazy(() => import("./pages/cleanMeals/CleanMealsBonusDetail"));
const CleanBakesBonuses  = lazy(() => import("./pages/cleanBakes/CleanBakesBonuses"));
const CleanBakesBonusDetail = lazy(() => import("./pages/cleanBakes/CleanBakesBonusDetail"));
const Kochgluck          = lazy(() => import("./pages/Kochgluck"));
const DailyPlan          = lazy(() => import("./pages/DailyPlan"));
const WeeklyPlan         = lazy(() => import("./pages/WeeklyPlan"));
const BonusTeDorado      = lazy(() => import("./pages/BonusTeDorado"));
const BonusListaNegra    = lazy(() => import("./pages/BonusListaNegra"));
const BonusRecetasMetabolicas = lazy(() => import("./pages/BonusRecetasMetabolicas"));
const Gracias            = lazy(() => import("./pages/Gracias"));
const SlugResolver       = lazy(() => import("./pages/SlugResolver"));
// Biblioteca page removed — route redirects to /recetas
const TodosContenidos    = lazy(() => import("./pages/TodosContenidos"));
// MeusConteudos (PT-BR legacy) removed — route redirects to /todos-los-contenidos
const ListaCompras       = lazy(() => import("./pages/ListaCompras"));
const Mais               = lazy(() => import("./pages/Mais"));
const Settings           = lazy(() => import("./pages/Settings"));
const SavedRecipes       = lazy(() => import("./pages/SavedRecipes"));
const Support            = lazy(() => import("./pages/Support"));
const Ayuda              = lazy(() => import("./pages/Ayuda"));
const GpHome             = lazy(() => import("./pages/GpHome"));
const RescuedSweetsPage  = lazy(() => import("./pages/RescuedSweetsPage"));
const TermsOfService     = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy      = lazy(() => import("./pages/PrivacyPolicy"));
const GpAnalyze          = lazy(() => import("./pages/GpAnalyze"));
const GpAddGlucose       = lazy(() => import("./pages/GpAddGlucose"));
const GpDashboard        = lazy(() => import("./pages/GpDashboard"));
const GpMeals            = lazy(() => import("./pages/GpMeals"));
const GpMealDetail       = lazy(() => import("./pages/GpMealDetail"));
const GpSettings         = lazy(() => import("./pages/GpSettings"));
const GpReports          = lazy(() => import("./pages/GpReports"));
const PublicRecipeDetail = lazy(() => import("./pages/PublicRecipeDetail"));
const UserRatingPage     = lazy(() => import("./components/UserRatingPage"));
const RecipeCreator      = lazy(() => import("./pages/RecipeCreator"));

/** Silently auto-sets locale from purchase data (renders nothing) */
const AutoLocale = () => { useAutoLocale(); return null; };

/**
 * Fires prefetch for the most critical queries as soon as the user is authenticated.
 * This warms the React Query cache before any page component mounts, so Home and
 * Recipes open with data already available (zero loading spinner on warm visits).
 * Renders nothing — purely a side-effect component.
 */
const PrefetchOnAuth = () => {
  const { isAuthenticated } = useAuth();
  const { language } = useLocale();
  const qc = useQueryClient();
  const prefetchedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || prefetchedRef.current) return;
    prefetchedRef.current = true;

    // 1. All recipes (shared with Home + Recipes pages)
    //
    // Este prefetch usa a MESMA chave de cache que a tela de Recetas, e roda
    // antes dela. Com `refetchOnMount: false` lá, o que estiver aqui é o que
    // a tela mostra — ela nunca chega a executar a própria consulta.
    //
    // Por isso o `.limit(1500)` que estava escrito aqui valia para o app
    // inteiro: o catálogo tem 2.496 receitas e quase mil nunca apareciam na
    // busca, sem erro nenhum e sem nada na tela indicando que faltava algo.
    // As colunas também precisam bater com as de lá, senão a tela recebe um
    // objeto sem os campos que espera.
    qc.prefetchQuery({
      queryKey: ["all-recipes-home"],
      queryFn: async () =>
        await buscarTodas<any>(
          "recipes",
          "id,title,image,image_group,category,total_time,time,prep_time,semaforo,module_id,language,recipe_number,sort_order,tags,meal_moment,difficulty,calories,glycemic_index,demand_score",
          (q) => q.order("demand_score", { ascending: false }).order("sort_order"),
        ),
    });

    // 2. Recipe modules — prefetched at login so home mounts without flash
    qc.prefetchQuery({
      queryKey: ["recipe-modules-home", language],
      queryFn: async () => {
        const { data } = await supabase
          .from("recipe_modules")
          .select("*")
          .eq("active", true)
          .order("sort_order");
        return data || [];
      },
      staleTime: 1000 * 60 * 60, // 60 min — cache survives typical session
    });

    // 3. Lite translation map for listing pages
    qc.prefetchQuery({
      queryKey: ["recipe-translations-titles", language],
      queryFn: async () => {
        const { data } = await supabase.from("recipe_translations")
          .select("recipe_id, title, tags")
          .eq("language", language)
          .range(0, 1999);
        const map = new Map<string, any>();
        (data || []).forEach((t: any) => map.set(t.recipe_id, t));
        return map;
      },
      staleTime: 1000 * 60 * 60,
    });

    // 3. App settings (logo, branding)
    qc.prefetchQuery({
      queryKey: ["app_settings"],
      queryFn: async () => {
        const { data } = await supabase.from("app_settings").select("key, value");
        const map: Record<string, string> = {};
        (data || []).forEach((r: any) => { map[r.key] = r.value; });
        return map;
      },
      staleTime: 1000 * 60 * 60,
    });

  }, [isAuthenticated, language, qc]);

  return null;
};


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 0,
      gcTime:    1000 * 60 * 30,       // 30 min in memory
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
    },
  },
});

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <GlucofySplashLoading />;
  if (!isAuthenticated) {
    const redirectTo = location.pathname + location.search + location.hash;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectTo)}`} replace />;
  }
  return <>{children}</>;
};

/**
 * App-wide popups and overlays. Clean Cakes e Clean Mornings já foram
 * "namespaces" com cromo próprio e sem estes avisos; hoje são produtos como
 * os outros, então os avisos valem neles também.
 */
const GlobalOverlays = () => {
  return (
    <>
      <DailyFeedbackPopup />
      <EarlySatisfactionPopup />
      <OnboardingExperience />
      <FeatureRatingManager />
      <AvisoDesignNovo />
    </>
  );
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <GlucofySplashLoading />;
  if (isAuthenticated) {
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get("redirect") || "/";
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div className="min-h-screen bg-background" />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

/**
 * A página do Clean Cakes: a coleção de sempre, agrupada pelas cinco
 * categorias da oferta e em inglês (o produto só existe em inglês). É um
 * produto como os outros: chega por Meus conteúdos, tem botão de voltar e
 * usa a navbar do app.
 */
const CleanCakesRoot = () => (
  <CollectionDetail routePrefix="/clean-cakes" lockedLocale="en" groupBy="category" />
);

const HomeRoute = () => <Inicio />;
const MyContentRoute = () => <TodosContenidos />;

/**
 * Aquece os pacotes das abas depois do login, na folga do navegador.
 *
 * As telas são lazy: sem isto, o primeiro toque em cada aba esperava o
 * pacote dela baixar, com o esqueleto no lugar. Com o aquecimento, os quatro
 * destinos da navbar (e o detalhe da receita, que é o toque seguinte) já
 * estão na memória quando a pessoa toca.
 */
const AquecerTelas = () => {
  const { isAuthenticated } = useAuth();
  useEffect(() => {
    if (!isAuthenticated) return;
    const aquecer = () => {
      void import("./pages/Recetas");
      void import("./pages/GpHome");
      void import("./pages/Mais");
      void import("./pages/RecipeDetail");
      void import("./pages/CollectionDetail");
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(aquecer, { timeout: 3000 });
      return;
    }
    const timer = setTimeout(aquecer, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);
  return null;
};




// Redirect components for legacy Spanish routes with path params
const RedirectToSupport = () => {
  const { threadId } = useParams<{ threadId: string }>();
  return <Navigate to={`/support/${threadId}`} replace />;
};
const RedirectRecipeCategory = () => {
  const { category } = useParams<{ category: string }>();
  return <Navigate to={`/recipes/${category}`} replace />;
};
const RedirectRecipeDetail = () => {
  const { category, recipeId } = useParams<{ category: string; recipeId: string }>();
  return <Navigate to={`/recipes/${category}/${recipeId}`} replace />;
};
const RedirectRecipeCooking = () => {
  const { category, recipeId } = useParams<{ category: string; recipeId: string }>();
  return <Navigate to={`/recipes/${category}/${recipeId}/cooking`} replace />;
};
const RedirectMyRecipe = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/my-recipes/${id}`} replace />;
};
const RedirectMyRecipeCooking = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/my-recipes/${id}/cooking`} replace />;
};

/**
 * A raiz de todas as rotas: os provedores que precisam do roteador, o cromo
 * (montado uma vez) e o Suspense em volta da tela.
 *
 * É uma rota de layout do roteador de dados (createBrowserRouter) e não um
 * <BrowserRouter>, porque só o roteador de dados sabe trocar de tela com
 * View Transition: o navegador fotografa a tela velha e faz o cross-fade
 * para a nova, sem nenhum frame vazio no meio. Com o <BrowserRouter> a tela
 * velha sumia num frame e a nova aparecia no seguinte — a "piscada".
 */
const Raiz = () => (
  <LocaleProvider>
  <AuthProvider>
    <ImageGenerationProvider>
    <AppConfigProvider>
      <DynamicHead />
      <ScrollToTop />
      <AutoLocale />
      <PrefetchOnAuth />
      <GlobalOverlays />
      <Chrome />
      <AvisoAtualizacao />
      <AquecerTelas />
      <Suspense fallback={<PageSkeleton />}>
        <Outlet />
      </Suspense>
    </AppConfigProvider>
    </ImageGenerationProvider>
  </AuthProvider>
  </LocaleProvider>
);

/** Um erro numa rota sobe para o ErrorBoundary do app, como sempre subiu. */
const ErroDeRota = () => {
  const erro = useRouteError();
  throw erro;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Raiz />} errorElement={<ErroDeRota />}>
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<ProtectedRoute><HomeRoute /></ProtectedRoute>} />
              <Route path="/recipes" element={<ProtectedRoute><Recetas /></ProtectedRoute>} />
              <Route path="/recipes/:category" element={<ProtectedRoute><RecipeCategory /></ProtectedRoute>} />
              <Route path="/recipes/:category/:recipeId" element={<ProtectedRoute><RecipeDetailErrorBoundary><RecipeDetail /></RecipeDetailErrorBoundary></ProtectedRoute>} />
              <Route path="/recipes/:category/:recipeId/cooking" element={<ProtectedRoute><RecipeCooking /></ProtectedRoute>} />
              <Route path="/more" element={<ProtectedRoute><Mais /></ProtectedRoute>} />
              <Route path="/my-content" element={<ProtectedRoute><MyContentRoute /></ProtectedRoute>} />
              <Route path="/shopping-list" element={<ProtectedRoute><ListaCompras /></ProtectedRoute>} />
              <Route path="/saved-recipes" element={<ProtectedRoute><SavedRecipes /></ProtectedRoute>} />
              <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
              <Route path="/support/:threadId" element={<ProtectedRoute><FeedbackThread /></ProtectedRoute>} />
              <Route path="/help" element={<ProtectedRoute><Ayuda /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
              <Route path="/panaderia-cero" element={<Navigate to="/panaderia" replace />} />
              
              <Route path="/glucophoto" element={<ProtectedRoute><GpHome /></ProtectedRoute>} />
              <Route path="/glucophoto/analyze" element={<ProtectedRoute><GpAnalyze /></ProtectedRoute>} />
              <Route path="/glucophoto/add-glucose" element={<ProtectedRoute><GpAddGlucose /></ProtectedRoute>} />
              <Route path="/glucophoto/dashboard" element={<ProtectedRoute><GpDashboard /></ProtectedRoute>} />
              <Route path="/glucophoto/meals" element={<ProtectedRoute><GpMeals /></ProtectedRoute>} />
              <Route path="/glucophoto/meals/:id" element={<ProtectedRoute><GpMealDetail /></ProtectedRoute>} />
              <Route path="/glucophoto/settings" element={<ProtectedRoute><GpSettings /></ProtectedRoute>} />
              <Route path="/glucophoto/reports" element={<ProtectedRoute><GpReports /></ProtectedRoute>} />
              <Route path="/create-recipe" element={<ProtectedRoute><RecipeCreator /></ProtectedRoute>} />
              <Route path="/my-recipes/:id" element={<ProtectedRoute><RecipeDetailErrorBoundary><RecipeDetail /></RecipeDetailErrorBoundary></ProtectedRoute>} />
              <Route path="/my-recipes/:id/cooking" element={<ProtectedRoute><RecipeCooking /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Perfil /></ProtectedRoute>} />
              <Route path="/rate" element={<ProtectedRoute><UserRatingPage /></ProtectedRoute>} />
              <Route path="/:slug/block/:blockId" element={<ProtectedRoute><BlockDetail /></ProtectedRoute>} />
              <Route path="/viewer/:id" element={<ProtectedRoute><Viewer /></ProtectedRoute>} />
              <Route path="/reposteria-cero" element={<ProtectedRoute><ReposteriaCero /></ProtectedRoute>} />
              <Route path="/reposteria-cero/:recipeId" element={<ProtectedRoute><ReposteriaCeroDetail /></ProtectedRoute>} />
              <Route path="/clean-remedies" element={<ProtectedRoute><RemedyLibrary namespace="clean_remedies" productId="8073539" locale="en" routePrefix="/clean-remedies" /></ProtectedRoute>} />
              <Route path="/clean-remedies/remedy/:slug" element={<ProtectedRoute><RemedyDetail namespace="clean_remedies" productId="8073539" locale="en" routePrefix="/clean-remedies" /></ProtectedRoute>} />
              <Route path="/clean-remedies/buried-list" element={<ProtectedRoute><RemedyBuriedList namespace="clean_remedies" productId="8073539" locale="en" routePrefix="/clean-remedies" /></ProtectedRoute>} />
              <Route path="/clean-remedies/how-to-measure" element={<ProtectedRoute><RemedyHowToMeasure namespace="clean_remedies" productId="8073539" locale="en" routePrefix="/clean-remedies" /></ProtectedRoute>} />
              <Route path="/saved-remedies" element={<ProtectedRoute><SavedRemedies /></ProtectedRoute>} />
              <Route path="/la-botica" element={<ProtectedRoute><RemedyLibrary namespace="la_botica" productId="8131891" locale="es" routePrefix="/la-botica" /></ProtectedRoute>} />
              <Route path="/la-botica/remedy/:slug" element={<ProtectedRoute><RemedyDetail namespace="la_botica" productId="8131891" locale="es" routePrefix="/la-botica" /></ProtectedRoute>} />
              <Route path="/la-botica/buried-list" element={<ProtectedRoute><RemedyBuriedList namespace="la_botica" productId="8131891" locale="es" routePrefix="/la-botica" /></ProtectedRoute>} />
              <Route path="/la-botica/how-to-measure" element={<ProtectedRoute><RemedyHowToMeasure namespace="la_botica" productId="8131891" locale="es" routePrefix="/la-botica" /></ProtectedRoute>} />
              {/* Clean Cakes — product 8495830. Self-contained namespace: English
                  only, Clean Cakes content only, no back button at the root.
                  Must stay above the /:slug catch-all further down. */}
              <Route path="/clean-cakes" element={<ProtectedRoute><CleanCakesRoot /></ProtectedRoute>} />
              <Route path="/clean-cakes/login" element={<PublicRoute><Login lockedLocale="en" defaultRedirect="/clean-cakes" /></PublicRoute>} />
              {/* Saved e More do namespace antigo: hoje são os do app. */}
              <Route path="/clean-cakes/saved" element={<Navigate to="/saved-recipes" replace />} />
              <Route path="/clean-cakes/more" element={<Navigate to="/more" replace />} />
              <Route path="/clean-cakes/bonuses" element={<ProtectedRoute><CleanCakesBonuses /></ProtectedRoute>} />
              <Route path="/clean-cakes/bonuses/:bonusSlug" element={<ProtectedRoute><CleanCakesBonusDetail /></ProtectedRoute>} />
              <Route path="/clean-meals/bonuses" element={<ProtectedRoute><CleanMealsBonuses /></ProtectedRoute>} />
              <Route path="/clean-meals/bonuses/:bonusSlug" element={<ProtectedRoute><CleanMealsBonusDetail /></ProtectedRoute>} />
              {/* Clean Bakes agrupa pela categoria, como o Clean Cakes: a oferta
                  é vendida em seis grupos nomeados (The Sunday griddle, The
                  American classics…), e o dish_type antigo (muffins, panes,
                  tortillas) os esconderia atrás de "Breads"/"Muffins". */}
              <Route path="/clean-bakes" element={<ProtectedRoute><CollectionDetail groupBy="category" /></ProtectedRoute>} />
              <Route path="/clean-bakes/bonuses" element={<ProtectedRoute><CleanBakesBonuses /></ProtectedRoute>} />
              <Route path="/clean-bakes/bonuses/:bonusSlug" element={<ProtectedRoute><CleanBakesBonusDetail /></ProtectedRoute>} />
              <Route path="/clean-cakes/recipes/:category/:recipeId" element={<ProtectedRoute><RecipeDetailErrorBoundary><RecipeDetail /></RecipeDetailErrorBoundary></ProtectedRoute>} />
              <Route path="/clean-cakes/recipes/:category/:recipeId/cooking" element={<ProtectedRoute><RecipeCooking /></ProtectedRoute>} />

              {/* Clean Mornings — namespace próprio, só inglês, no molde do Clean Cakes.
                  Tudo que a VSL v1.2 promete mora aqui: src/lib/cleanMornings.ts. */}
              <Route path={CEM_PREFIX} element={<ProtectedRoute><CleanMorningsHome /></ProtectedRoute>} />
              <Route path={`${CEM_PREFIX}/login`} element={<PublicRoute><Login lockedLocale="en" defaultRedirect={CEM_PREFIX} /></PublicRoute>} />
              <Route path={`${CEM_PREFIX}/recipes/:category`} element={<ProtectedRoute><CleanMorningsCategoria /></ProtectedRoute>} />
              <Route path={`${CEM_PREFIX}/recipes/:category/:recipeId`} element={<ProtectedRoute><RecipeDetailErrorBoundary><RecipeDetail /></RecipeDetailErrorBoundary></ProtectedRoute>} />
              <Route path={`${CEM_PREFIX}/recipes/:category/:recipeId/cooking`} element={<ProtectedRoute><RecipeCooking /></ProtectedRoute>} />
              <Route path={`${CEM_PREFIX}/dough`} element={<ProtectedRoute><CleanMorningsMassa /></ProtectedRoute>} />
              <Route path={`${CEM_PREFIX}/snacks`} element={<ProtectedRoute><CleanMorningsLanches /></ProtectedRoute>} />
              <Route path={`${CEM_PREFIX}/saved`} element={<Navigate to="/saved-recipes" replace />} />
              <Route path={`${CEM_PREFIX}/more`} element={<Navigate to="/more" replace />} />
              <Route path={`${CEM_PREFIX}/bonuses`} element={<ProtectedRoute><CleanMorningsBonuses /></ProtectedRoute>} />
              <Route path={`${CEM_PREFIX}/bonuses/:bonusSlug`} element={<ProtectedRoute><CleanMorningsBonusDetail /></ProtectedRoute>} />
              <Route path={`${CEM_PREFIX}/grocery-list`} element={<ProtectedRoute><ListaCompras /></ProtectedRoute>} />
              {/* German market products */}
              <Route path="/kuchengluck" element={<ProtectedRoute><Kuchengluck /></ProtectedRoute>} />
              <Route path="/brotgluck" element={<ProtectedRoute><Brotgluck /></ProtectedRoute>} />
              <Route path="/kochgluck" element={<ProtectedRoute><Kochgluck /></ProtectedRoute>} />
              <Route path="/bonus/te-dorado" element={<ProtectedRoute><BonusTeDorado /></ProtectedRoute>} />
              <Route path="/bonus/lista-negra" element={<ProtectedRoute><BonusListaNegra /></ProtectedRoute>} />
              <Route path="/bonus/recetas-metabolicas" element={<ProtectedRoute><BonusRecetasMetabolicas /></ProtectedRoute>} />
              <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
              {/* Meal Planning */}
              <Route path="/daily-plan" element={<ProtectedRoute><DailyPlan /></ProtectedRoute>} />
              <Route path="/weekly-plan" element={<ProtectedRoute><WeeklyPlan /></ProtectedRoute>} />

              <Route path="/rescued-sweets" element={<ProtectedRoute><RescuedSweetsPage /></ProtectedRoute>} />

              {/* Public recipe share page — no auth required */}
              <Route path="/r/:recipeSlug" element={<PublicRecipeDetail />} />

              {/* Public legal pages — sem login */}
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              {/* /terms and /privacy removed — use /terms-of-service and /privacy-policy directly */}

              <Route path="/thank-you" element={<Gracias />} />
              <Route path="/chef.natasha.perez" element={<LinkTree />} />
              {/* ── Legacy Spanish route redirects (must come before /:slug) ── */}
              <Route path="/inicio" element={<Navigate to="/home" replace />} />
              <Route path="/recetas" element={<Navigate to="/recipes" replace />} />
              <Route path="/recetas/:category" element={<RedirectRecipeCategory />} />
              <Route path="/recetas/:category/:recipeId" element={<RedirectRecipeDetail />} />
              <Route path="/recetas/:category/:recipeId/cooking" element={<RedirectRecipeCooking />} />
              <Route path="/mais" element={<Navigate to="/more" replace />} />
              <Route path="/todos-los-contenidos" element={<Navigate to="/my-content" replace />} />
              <Route path="/meus-conteudos" element={<Navigate to="/my-content" replace />} />
              <Route path="/lista-compras" element={<Navigate to="/shopping-list" replace />} />
              <Route path="/recetas-guardadas" element={<Navigate to="/saved-recipes" replace />} />
              <Route path="/soporte" element={<Navigate to="/support" replace />} />
              <Route path="/soporte/:threadId" element={<RedirectToSupport />} />
              <Route path="/feedback/:threadId" element={<RedirectToSupport />} />
              <Route path="/ayuda" element={<Navigate to="/help" replace />} />
              <Route path="/configuraciones" element={<Navigate to="/settings" replace />} />
              <Route path="/crear-receta" element={<Navigate to="/create-recipe" replace />} />
              <Route path="/mis-recetas/:id" element={<RedirectMyRecipe />} />
              <Route path="/mis-recetas/:id/cooking" element={<RedirectMyRecipeCooking />} />
              <Route path="/perfil" element={<Navigate to="/profile" replace />} />
              <Route path="/valorar" element={<Navigate to="/rate" replace />} />
              <Route path="/gracias" element={<Navigate to="/thank-you" replace />} />
              <Route path="/biblioteca" element={<Navigate to="/recipes" replace />} />
              {/* Dynamic slug resolver — products & collections — must be near the end */}
              <Route path="/:slug" element={<ProtectedRoute><SlugResolver /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
    </Route>,
  ),
);

/**
 * Toda navegação troca de tela com View Transition (cross-fade do navegador,
 * ~250ms, sem frame vazio). Redirecionamentos (`replace`) não: são um pulo
 * técnico, e animar dois pulos seguidos faria a tela piscar duas vezes.
 * Onde o navegador não tem a API (iPhone antes do iOS 18), a troca é
 * instantânea, como era.
 */
type NavegarDoRoteador = typeof router.navigate;
type OpcoesDeNavegacao = { replace?: boolean; viewTransition?: boolean } & Record<string, unknown>;
const navegarSemTransicao: NavegarDoRoteador = router.navigate.bind(router);
router.navigate = ((to: number | Parameters<NavegarDoRoteador>[0], opts?: OpcoesDeNavegacao) =>
  typeof to === "number"
    ? navegarSemTransicao(to)
    : navegarSemTransicao(to, { viewTransition: !opts?.replace, ...opts })) as NavegarDoRoteador;

const App = () => (
  <ErrorBoundary>
  <ThemeProvider>
  <TamanhoTextoProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <RouterProvider router={router} />
    </TooltipProvider>
  </QueryClientProvider>
  </TamanhoTextoProvider>
  </ThemeProvider>
  </ErrorBoundary>
);

export default App;
